import { useState, useCallback, useRef } from "react";
import {
  RCAAnalyzeRequest,
  RCAResult,
  RCAProgressUpdate,
  startRCAAnalysis,
  streamRCAProgress,
} from "../../shared/rcaApiService";
import { getIdToken } from "../../shared/apiService";

export default function useRCAProgress() {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [result, setResult] = useState<RCAResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const startAnalysis = useCallback(async (request: RCAAnalyzeRequest) => {
    setProgress(0);
    setPhase("initializing");
    setMessage("Starting analysis...");
    setResult(null);
    setError(null);
    setIsLoading(true);

    // Cancel any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Fetch token once and reuse for both the POST and SSE connection
      // This avoids a double OfficeRuntime.storage call which can stall in WebView2
      const token = await getIdToken();

      // Start the analysis job
      const jobResponse = await startRCAAnalysis(request, token);
      const jobId = jobResponse.job_id;

      console.log("📊 RCA job started:", jobId);

      // Handle progress updates
      const handleProgress = (update: RCAProgressUpdate) => {
        console.log("📊 RCA progress:", update);

        if (update.progress !== undefined && update.progress !== null) {
          setProgress(update.progress);
        }

        if (update.phase) {
          setPhase(update.phase);
        }

        if (update.message) {
          setMessage(update.message);
        }

        if (update.status === "completed" && update.result) {
          console.log("✅ RCA analysis completed");
          setResult(update.result);
          setIsLoading(false);
          abortControllerRef.current = null;
        }

        if (update.status === "failed") {
          console.error("❌ RCA analysis failed:", update.error);
          setError(update.error || "Analysis failed");
          setIsLoading(false);
          abortControllerRef.current = null;
        }
      };

      // Handle errors
      const handleError = (errorMessage: string) => {
        setError(errorMessage);
        setIsLoading(false);
        abortControllerRef.current = null;
      };

      // Start streaming progress with Authorization header (reusing token)
      await streamRCAProgress(
        jobId,
        handleProgress,
        handleError,
        abortController.signal,
        token
      );

    } catch (err) {
      console.error("RCA analysis error:", err);
      setError(err instanceof Error ? err.message : "Failed to start analysis");
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, []);

  const stopListening = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
  }, []);

  const reset = useCallback(() => {
    stopListening();
    setProgress(0);
    setPhase("");
    setMessage("");
    setResult(null);
    setError(null);
  }, [stopListening]);

  return {
    progress,
    phase,
    message,
    result,
    isLoading,
    error,
    startAnalysis,
    stopListening,
    reset,
  };
}
